import React from 'react';
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './App.css';
import theme from './Styles/theme';
import GlobalStateProvider from './Contexts/GlobalStateProvider';
import Header from './Components/Header';
import Footer from './Components/Footer';
import Home from './Components/Home';
import Profile from './Components/Profile';
import Feltarasok from './Components/Feltarasok/Feltarasok';
import MineralWaterDetail from './Components/Feltarasok/MineralWaterDetail';
import Gyujtemenyek from './Components/Gyujtemenyek';
import SajtoRadar from './Components/SajtoRadar';
import GSST from './Components/GSST';
import Talalkozo from './Components/Talalkozo';

import Login from './Components/Administration/Login';
import Register from './Components/Administration/Register';
import Activation from './Components/Administration/Activation';
import PasswordReset from './Components/Administration/PasswordReset';
import PasswordResetConfirm from './Components/Administration/PasswordResetConfirm';
import AccountCreated from './Components/Administration/AccountCreated';
import ProfileUpdate from './Components/Administration/ProfileUpdate';
import MembershipAction from './Components/Administration/MembershipAction';

function AppContent() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isMapPage = location.pathname.includes('/feltarasok') || location.pathname.includes('/gsst');

  // Helper to normalize language codes (e.g., hu-HU -> hu)
  const normalizeLang = (lang) => {
    if (!lang) return i18n.language || 'hu';
    const baseLang = lang.split('-')[0].toLowerCase();
    const supportedLangs = ['hu', 'en', 'ro'];
    return supportedLangs.includes(baseLang) ? baseLang : 'hu';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Routes>
          {/* Language Redirect Component */}
          <Route path="/:lang/*" element={<LanguageRedirect normalizeLang={normalizeLang} />} />
          
          <Route path="/:lang/" element={<Home />} />
          <Route path="/:lang/rolunk" element={<Box sx={{ p: 4 }}>{t('Header.AboutUs')} {t('App.UnderConstruction')}</Box>} />
          <Route path="/:lang/sajtoradar" element={<SajtoRadar />} />
          <Route path="/:lang/feltarasok" element={<Feltarasok />} />
          <Route path="/:lang/gyujtemenyek" element={<Gyujtemenyek />} />
          <Route path="/:lang/mineral_water/:category/:id" element={<MineralWaterDetail />} />
          <Route path="/:lang/talalkozo" element={<Talalkozo />} />
          <Route path="/:lang/kirandulasok" element={<Box sx={{ p: 4 }}>{t('App.TripsUnderConstruction')}</Box>} />
          <Route path="/:lang/gsst" element={<GSST />} />
          <Route path="/:lang/profile" element={<Profile />} />
          <Route path="/:lang/login" element={<Login />} />
          <Route path="/:lang/register" element={<Register />} />
          <Route path="/:lang/activate/:uid/:token" element={<Activation />} />
          <Route path="/:lang/password-reset" element={<PasswordReset />} />
          <Route path="/:lang/password-reset-confirm/:uid/:token" element={<PasswordResetConfirm />} />
          <Route path="/:lang/account-created" element={<AccountCreated />} />
          <Route path="/:lang/profile-update" element={<ProfileUpdate />} />
          <Route path="/:lang/membership/:action/:userId/:token/" element={<MembershipAction />} />
          
          {/* Fallback for routes without lang prefix */}
          <Route path="/" element={<Navigate to={`/${i18n.language}/`} replace />} />
          <Route path="/rolunk" element={<Navigate to={`/${i18n.language}/rolunk`} replace />} />
          <Route path="/sajtoradar" element={<Navigate to={`/${i18n.language}/sajtoradar`} replace />} />
          <Route path="/feltarasok" element={<Navigate to={`/${i18n.language}/feltarasok`} replace />} />
          <Route path="/gyujtemenyek" element={<Navigate to={`/${i18n.language}/gyujtemenyek`} replace />} />
          <Route path="/talalkozo" element={<Navigate to={`/${i18n.language}/talalkozo`} replace />} />
          <Route path="/gsst" element={<Navigate to={`/${i18n.language}/gsst`} replace />} />
          <Route path="/profile" element={<Navigate to={`/${i18n.language}/profile`} replace />} />
          <Route path="/login" element={<Navigate to={`/${i18n.language}/login`} replace />} />

          <Route path="*" element={<Navigate to={`/${i18n.language}/`} replace />} />
        </Routes>
      </Box>
      {!isMapPage && <Footer />}
    </Box>
  );
}

function LanguageRedirect({ normalizeLang }) {
  const { lang } = useParams();
  const location = useLocation();
  const normalized = normalizeLang(lang);

  if (lang !== normalized) {
    const newPath = location.pathname.replace(`/${lang}`, `/${normalized}`);
    return <Navigate to={newPath} replace />;
  }
  return null;
}

function App() {
  return (
    <GlobalStateProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </GlobalStateProvider>
  );
}

export default App;
